/**
 * TwoFAVerifyScreen — second leg of the 2FA login flow.
 *
 * The user landed here from LoginScreen carrying a partial_token in
 * react-router state. They enter their 6-digit code, we hit /2fa/verify,
 * persist the access token, and bounce them to whatever page they were
 * trying to reach (or "/" by default).
 *
 * If the partial_token is missing or expired, we send them back to /login.
 */

import { Anchor, Button, Container, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiError, api, auth, type MeResponse } from "../lib/api";

export function TwoFAVerifyScreen({ onLoggedIn }: { onLoggedIn: (me: MeResponse) => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const partialToken = (location.state as { partialToken?: string } | null)?.partialToken;

  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!partialToken) {
    return <Navigate to="/login" replace />;
  }

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const tokens = await api.twoFAVerifyLogin(partialToken, code);
      auth.setTokens(tokens);
      const me = await api.me();
      onLoggedIn(me);
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.detail === "invalid_totp_code") {
        setError("קוד שגוי. נסה שוב.");
      } else if (err instanceof ApiError && err.detail === "invalid_partial_token") {
        setError("פג תוקף הסשן. כניסה מחדש נדרשת.");
        setTimeout(() => navigate("/login", { replace: true }), 1500);
      } else {
        setError("משהו השתבש. נסה שוב.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container size="xs" py="xl">
      <Paper p="lg" radius="md" withBorder>
        <Stack>
          <Title order={2}>אימות דו־שלבי</Title>
          <Text c="dimmed" size="sm">הזן קוד 6 ספרות מאפליקציית האימות.</Text>
          <form onSubmit={handle}>
            <Stack>
              <TextInput
                label="קוד"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                autoComplete="one-time-code"
                dir="ltr"
                maxLength={6}
                required
                autoFocus
              />
              {error && (
                <Text c="red" size="sm">
                  {error}
                </Text>
              )}
              <Button type="submit" loading={busy} disabled={code.length !== 6} fullWidth>
                אמת
              </Button>
              <Anchor
                component={Link}
                to="/2fa/recovery"
                state={{ partialToken }}
                size="sm"
              >
                איבדת גישה לאפליקציה? השתמש בקוד שחזור
              </Anchor>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
}
